import { ItemObject } from "@/layouts/drawer/use-drawer-data";
import { DataCollectionState, useScreenSize } from "@soft-db/ui";
import { defineStore } from "pinia";
import { useFeaturesStore } from "./features";

export enum DrawerViewType {
	ALL,
	FAVORITES,
}

export const useDrawerStore = defineStore("drawer", () => {
	const desktopOpen = useLocalStorage("drawer", false);
	const mobileOpen = ref(false);

	const { mdOrLower } = useScreenSize();

	const open = computed(() => {
		if (mdOrLower.value) {
			return mobileOpen.value;
		} else {
			return desktopOpen.value;
		}
	});

	watch(open, (open, before) => {
		if (before && !open) {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
		}
	});

	function toggleDrawer() {
		if (mdOrLower.value) {
			mobileOpen.value = !mobileOpen.value;
		} else {
			desktopOpen.value = !desktopOpen.value;
		}
	}

	const _view = ref<DrawerViewType>(DrawerViewType.FAVORITES);

	const featureStore = useFeaturesStore();
	const favoriteEnabled = computed(() => featureStore.hasFeature("favorite"));

	const view = computed({
		get() {
			if (favoriteEnabled.value) {
				return _view.value;
			} else {
				return DrawerViewType.ALL;
			}
		},
		set(view) {
			if (favoriteEnabled.value) {
				_view.value = view;
			}
		},
	});

	const isFavorites = computed(() => view.value === DrawerViewType.FAVORITES);

	function toAll() {
		view.value = DrawerViewType.ALL;
	}

	function toFavorites() {
		view.value = DrawerViewType.FAVORITES;
	}

	return {
		open,
		desktopOpen,
		mobileOpen,
		_view,
		view,
		toggleDrawer,
		isFavorites,
		favoriteEnabled,
		toAll,
		toFavorites,
	};
});

export const useDrawerState = defineStore("drawer-state", () => {
	const isLoading = ref(false);
	const items = ref<ItemObject[]>([]);

	const filter = ref<string>("");

	type ColumnFilter = DataCollectionState["fieldFilters"][number];
	type FieldFilters = Record<string, ColumnFilter>;
	const fieldFilters = ref<FieldFilters>({});

	function getFilters(cols: string[]) {
		return computed({
			get() {
				const out: FieldFilters = {};
				for (const col of cols) {
					if (fieldFilters.value[col] != null) {
						out[col] = fieldFilters.value[col];
					}
				}
				return Object.values(out);
			},
			set(newFieldFilters) {
				const ids = new Set(newFieldFilters.map((f) => f.id));

				for (const filter of newFieldFilters) {
					fieldFilters.value[filter.id] = filter;
				}

				// Delete removed filters
				for (const col of cols) {
					if (!ids.has(col)) {
						delete fieldFilters.value[col];
					}
				}
			},
		});
	}

	function clear() {
		filter.value = "";
		fieldFilters.value = {};
	}

	return {
		isLoading,
		items,
		fieldFilters,
		getFilters,
		filter,
		clear,
	};
});
